import { useCallback } from "react";
import { XCircle } from "lucide-react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  getOrderCloseConfirmOptions,
  isClosingStatus,
  OrderConfirmLabelSource,
} from "@/lib/orderCloseConfirm";
import type { OrderStatus } from "@/redux/services/order/order.type";

/**
 * Result of the confirmation step before a status change. Call sites spread
 * the extra fields into `changeOrderStatus`, so a later variant (e.g. one
 * carrying an API acknowledgement flag) needs no call-site change.
 */
export type OrderCloseConfirmResult = { proceed: false } | { proceed: true };

/**
 * Single confirmation step for every order status change made from the UI
 * (SFRESTO-148 / SFRESTO-84). Canceling or rejecting always asks first; other
 * statuses proceed without a dialog. Used by `OrderCard`,
 * `OrderDetailsDrawer` (Commandes page and POS drawer) and `KdsOrderCard`.
 *
 * Extend this hook rather than adding a second dialog at the call sites: a
 * more specific warning (for example an order that already holds collected
 * money) should replace the generic wording here, so the user sees one dialog.
 */
export const useOrderCloseConfirm = () => {
  const confirm = useConfirm();

  return useCallback(
    async (
      order: OrderConfirmLabelSource,
      status: OrderStatus
    ): Promise<OrderCloseConfirmResult> => {
      if (!isClosingStatus(status)) return { proceed: true };

      const ok = await confirm({
        ...getOrderCloseConfirmOptions(order, status),
        icon: <XCircle className="h-6 w-6" />,
      });
      return ok ? { proceed: true } : { proceed: false };
    },
    [confirm]
  );
};
